import { createContext, useContext, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useLoggingFlow } from "./logging-flow";

// Native photo-picker contract. The hidden capture/library inputs live here —
// always mounted inside LoggingFlowProvider — so the Quick Log launcher can
// open the OS camera / file chooser DIRECTLY (no intermediate flow screen).
// A chosen file starts the photo/upload flow with the file in the payload;
// cancelling the picker changes nothing.

// Same detection the photo flow used: coarse pointer or a mobile UA.
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;
  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  const mobileUa = /Android|iPhone|iPad/i.test(window.navigator.userAgent);
  return coarsePointer || mobileUa;
}

export interface PhotoPickerContextValue {
  // Opens the OS file chooser (image library). Works on every platform.
  readonly openLibraryPicker: () => void;
  // Opens the native camera capture sheet (mobile only — desktop callers
  // keep the in-app webcam flow instead).
  readonly openCapturePicker: () => void;
}

const PhotoPickerContext = createContext<PhotoPickerContextValue | null>(null);

export function PhotoPickerProvider({ children }: { readonly children: ReactNode }) {
  const { startFlow } = useLoggingFlow();
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const captureInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (kind: "photo" | "upload") => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately so picking the same file twice re-fires change.
    event.target.value = "";
    if (!file) {
      console.debug("[Frontend][QuickLog] Native picker closed without a file", { kind });
      return;
    }
    console.log("[Frontend][QuickLog] Photo chosen via native picker", {
      kind,
      fileName: file.name,
      fileSize: file.size,
    });
    startFlow(kind, { file });
  };

  const value = useMemo<PhotoPickerContextValue>(
    () => ({
      openLibraryPicker: () => {
        console.debug("[Frontend][QuickLog] Opening native library picker");
        libraryInputRef.current?.click();
      },
      openCapturePicker: () => {
        console.debug("[Frontend][QuickLog] Opening native camera picker");
        captureInputRef.current?.click();
      },
    }),
    [],
  );

  return (
    <PhotoPickerContext.Provider value={value}>
      {children}
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        data-testid="photo-picker-library-input"
        onChange={handleChange("upload")}
        className="hidden"
      />
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        data-testid="photo-picker-capture-input"
        onChange={handleChange("photo")}
        className="hidden"
      />
    </PhotoPickerContext.Provider>
  );
}

export function usePhotoPicker(): PhotoPickerContextValue {
  const context = useContext(PhotoPickerContext);
  if (context === null) {
    throw new Error(
      "usePhotoPicker must be used within a PhotoPickerProvider (mounted in App inside LoggingFlowProvider)",
    );
  }
  return context;
}
